import React, { useCallback } from 'react';
import { DeleteModal, ProviderLayout, UpliftThemeProvider, useAlert } from '@uplift/ui-v2';
import { ClaimCorrectionNoteContent } from './claim-correction.content';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteCommunicationNote } from '@uplift/api';
import { useHistory } from 'react-router';
import { providerPatientRoute } from '@uplift/constants';

export type ClaimCorrectionProps = {
  patientId: string;
  noteId: string;
};

export const ClaimCorrectionsPage: React.VFC<ClaimCorrectionProps> = ({ patientId, noteId }) => {
  const [ deleteOpen, setDeleteOpen ] = React.useState(false);
  const queryClient = useQueryClient();
  const history = useHistory();
  const snackbar = useAlert();

  const handleOpen = useCallback(() => {
    setDeleteOpen(true);
  }, []);

  const deleteNoteMutation = useMutation({
    mutationFn: () => deleteCommunicationNote(noteId),
    onSuccess: () => {
      queryClient.invalidateQueries([ 'medical-notes', noteId ]);

      history.push(providerPatientRoute.createLink({ patientId }));
      snackbar.success('Claim Correction note deleted');
    }
  });

  const handleModalClose = useCallback(async (proceed: boolean) => {
    if (proceed) {
      await deleteNoteMutation.mutateAsync();
    }

    setDeleteOpen(false);
  }, [ deleteNoteMutation ]);

  return (
    <ProviderLayout
      logoLink=""
      background="white"
      navigationVariant="clear"
      showClientsSearch={false}
      showNavigationLinks={false}
    >
      <UpliftThemeProvider>
        <ClaimCorrectionNoteContent
          noteId={noteId}
          handleOpen={handleOpen}
          patientId={patientId}
        />
        <DeleteModal
          open={deleteOpen}
          title="Delete this note?"
          onClose={handleModalClose}
          loading={deleteNoteMutation.isLoading}
        />
      </UpliftThemeProvider>
    </ProviderLayout>
  );
};
